(() => {
  const ENDPOINT = 'https://checkout.nuovalucebeauty.com/api/checkout/session';
  const TEMP_UNAVAILABLE_MESSAGE = ['Secure checkout is temporarily unavailable', 'Please refresh and try again.'].join('. ');
  let busy = false;

  function showCheckoutError(message) {
    // Never send Nuovaluce customers to Shopify checkout. Stripe is the checkout of record.
    // If a handoff fails, keep the customer on-site and expose a lightweight inline message.
    const id = 'nuovaluce-stripe-checkout-error';
    let box = document.getElementById(id);
    if (!box) {
      box = document.createElement('div');
      box.id = id;
      box.setAttribute('role', 'alert');
      box.style.cssText = 'position:fixed;z-index:2147483647;left:16px;right:16px;bottom:16px;padding:14px 16px;border-radius:10px;background:#fff3f3;color:#7a1111;border:1px solid #e5b4b4;box-shadow:0 8px 30px rgba(0,0,0,.18);font:14px/1.4 system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;';
      document.body.appendChild(box);
    }
    box.textContent = message || 'Stripe checkout could not open. Please refresh and try again.';
  }

  const nativeAlert = window.alert && window.alert.bind(window);
  window.alert = function nuovaluceCheckoutAlertGuard(message) {
    if (String(message || '').includes(TEMP_UNAVAILABLE_MESSAGE)) {
      showCheckoutError('Stripe checkout could not open. Please refresh and try again.');
      return;
    }
    return nativeAlert ? nativeAlert(message) : undefined;
  };

  function checkoutText(el) {
    return String((el && (el.textContent || el.value || el.getAttribute?.('aria-label') || el.getAttribute?.('data-testid') || el.getAttribute?.('class') || '')) || '').trim().toLowerCase();
  }

  function isCheckoutUrl(url) {
    const value = String(url || '').toLowerCase();
    return value === '/checkout' || value.endsWith('/checkout') || value.includes('/checkout?') || value.includes('/checkouts/') || value.includes('/cart/checkout');
  }

  function isCheckoutTrigger(el) {
    if (!el) return false;
    const name = (el.getAttribute('name') || '').toLowerCase();
    const value = (el.getAttribute('value') || '').toLowerCase();
    const href = el.getAttribute('href') || '';
    const action = el.getAttribute('action') || '';
    const aria = (el.getAttribute('aria-label') || '').toLowerCase();
    const text = checkoutText(el);
    return name === 'checkout' || value === 'checkout' || isCheckoutUrl(href) || isCheckoutUrl(action) || text === 'checkout' || text === 'check out' || text.includes('secure checkout') || text.includes('opening secure checkout') || aria === 'checkout' || aria === 'check out';
  }

  function hideAcceleratedCheckout() {
    const styleId = 'nuovaluce-external-stripe-hide-accelerated-checkout';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      shopify-accelerated-checkout,
      shopify-buy-it-now-button,
      .shopify-payment-button,
      .shopify-payment-button__button,
      [data-testid="ShopifyPay-button"],
      [data-shopify="payment-button"],
      [data-testid*="accelerated" i] { display: none !important; visibility: hidden !important; }
    `;
    document.head.appendChild(style);
  }

  function clearUnsupportedDiscountState() { /* Shopify/Stripe now validate codes server-side. Do not clear customer-entered codes client-side. */ }

  function hideUnsupportedDiscountToast() {
    clearUnsupportedDiscountState();
    document.querySelectorAll('body *').forEach((el) => {
      const text = (el.textContent || '').trim().toLowerCase();
      if (text === 'unsupported discount code: july4' || text === 'unsupported discount code: july4×' || text.includes('unsupported discount code: july4')) {
        el.style.setProperty('display', 'none', 'important');
        el.setAttribute('aria-hidden', 'true');
      }
    });
  }

  async function getCartLines() {
    const res = await fetch('/cart.js', { credentials: 'same-origin', headers: { accept: 'application/json' } });
    if (!res.ok) throw new Error('Could not read cart');
    const cart = await res.json();
    const lines = (cart.items || []).map((item) => ({ variantId: String(item.variant_id), quantity: Number(item.quantity || 1) }));
    if (!lines.length) throw new Error('Cart is empty');
    return { cart, lines };
  }

  function readDiscountFromStorage() {
    try {
      const keys = ['nuovaluce_discount_code', 'stickycart-discount-code', 'discount_code', 'discountCode', 'coupon_code', 'couponCode'];
      for (const key of keys) {
        const value = String(localStorage.getItem(key) || '').trim();
        if (value) return value;
      }
    } catch (_) {}
    return '';
  }

  function cleanDiscountCode(value) {
    const code = String(value || '').trim();
    if (!/^[A-Za-z0-9_-]{3,40}$/.test(code)) return '';
    if (/^(off|free|discount|coupon|promo|code|sitewide|shipping|limited|stock)$/i.test(code)) return '';
    return code;
  }

  function discountCodeFromApplication(app) {
    if (!app) return '';
    return cleanDiscountCode(app.code || app.discount_code || '');
  }


  function readDiscountFromDomText() {
    try {
      const nodes = [...document.querySelectorAll('[data-discount-code], [class*="discount" i], [id*="discount" i], [class*="coupon" i], [id*="coupon" i], .announcement-bar, .top-bar, body')];
      for (const el of nodes) {
        const attr = String(el.getAttribute('data-discount-code') || '').trim();
        if (cleanDiscountCode(attr)) return cleanDiscountCode(attr);
        const text = String(el.textContent || '').replace(/\s+/g, ' ').trim();
        const match = text.match(/(?:discount\s*code|coupon\s*code|promo\s*code|code)\s*[:\-]?\s*([A-Za-z0-9][A-Za-z0-9_-]{2,39})/i);
        if (match && cleanDiscountCode(match[1])) return cleanDiscountCode(match[1]);
      }
    } catch (_) {}
    return '';
  }

  function getDiscountCode(cart) {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = cleanDiscountCode(params.get('discount') || params.get('discount_code') || params.get('coupon'));
    const input = document.querySelector('[name="discount"], [name="discount_code"], input[placeholder*="discount" i], input[placeholder*="coupon" i]');
    const fromInput = cleanDiscountCode(input && input.value);
    const cartApplications = [
      ...(cart && Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
      ...(cart && Array.isArray(cart.discount_applications) ? cart.discount_applications : [])
    ];
    let fromCart = '';
    for (const app of cartApplications) {
      fromCart = discountCodeFromApplication(app);
      if (fromCart) break;
    }
    if (!fromCart && cart && Array.isArray(cart.items)) {
      for (const item of cart.items) {
        const apps = [...(item.discounts || []), ...(item.line_level_discount_allocations || []).map(a => a.discount_application || a)];
        for (const app of apps) {
          fromCart = discountCodeFromApplication(app);
          if (fromCart) break;
        }
        if (fromCart) break;
      }
    }
    const stored = cleanDiscountCode(readDiscountFromStorage());
    const fromDom = readDiscountFromDomText();
    const code = String(fromUrl || fromInput || fromCart || stored || fromDom || '').trim();
    if (code) {
      try { localStorage.setItem('nuovaluce_discount_code', code); } catch (_) {}
      return code;
    }
    return undefined;
  }


  function readVisibleDiscountedSubtotal() {
    try {
      const text = document.body ? document.body.innerText || '' : '';
      const subtotalIndex = text.toLowerCase().lastIndexOf('subtotal');
      const near = subtotalIndex >= 0 ? text.slice(subtotalIndex, subtotalIndex + 250) : text;
      const amounts = [...near.matchAll(/\$\s*([0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]{2})?)/g)].map(m => Math.round(Number(m[1].replace(/,/g, '')) * 100)).filter(n => Number.isFinite(n) && n >= 0);
      return amounts.length ? Math.min(...amounts) : undefined;
    } catch (_) { return undefined; }
  }

  function patchCheckoutDestinations() {
    document.querySelectorAll('a[href], form[action]').forEach((el) => {
      if (isCheckoutUrl(el.getAttribute('href')) || isCheckoutUrl(el.getAttribute('action'))) {
        el.dataset.nlcOriginalCheckoutTarget = el.getAttribute('href') || el.getAttribute('action') || '';
        if (el.tagName === 'A') el.setAttribute('href', '#nuovaluce-stripe-checkout');
        if (el.tagName === 'FORM') el.setAttribute('action', '/cart');
      }
    });
  }

  function setBusy(on) {
    busy = on;
    document.querySelectorAll('[name="checkout"], a[href$="/checkout"], button, input[type="submit"]').forEach((el) => {
      if (!isCheckoutTrigger(el)) return;
      if ('disabled' in el) el.disabled = on;
      el.setAttribute('aria-busy', on ? 'true' : 'false');
      if (on) el.dataset.nlcOldText = el.textContent || el.value || '';
      if (on && el.textContent) el.textContent = 'Opening secure checkout…';
      if (on && el.value) el.value = 'Opening secure checkout…';
      if (!on && el.dataset.nlcOldText) {
        if (el.textContent) el.textContent = el.dataset.nlcOldText;
        if (el.value) el.value = el.dataset.nlcOldText;
      }
    });
  }

  async function openStripeCheckout(event) {
    if (busy) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    setBusy(true);
    try {
      const { cart, lines } = await getCartLines();
      const email = cart.email || undefined;
      const cartPricing = (cart.items || []).map((item) => ({
        variantId: String(item.variant_id),
        quantity: Number(item.quantity || 1),
        finalPrice: Number(item.final_price),
        finalLinePrice: Number(item.final_line_price),
        originalPrice: Number(item.original_price),
        originalLinePrice: Number(item.original_line_price),
        lineLevelTotalDiscount: Number(item.line_level_total_discount || 0),
        totalDiscount: Number(item.total_discount || 0),
        productTitle: item.product_title,
        variantTitle: item.variant_title || '',
        discounts: item.discounts || [],
        lineLevelDiscountAllocations: item.line_level_discount_allocations || []
      }));
      const visibleSubtotal = readVisibleDiscountedSubtotal();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({ shopifyCartId: cart.token, customerEmail: email, lines, cartPricing, discountCode: getDiscountCode(cart), visibleSubtotal })
      }).finally(() => clearTimeout(timeoutId));
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.error || 'Checkout unavailable');
      window.location.assign(data.url);
    } catch (err) {
      console.error('[Nuovaluce checkout]', err);
      setBusy(false);
      showCheckoutError(err && err.message ? err.message : 'Stripe checkout could not open. Please refresh and try again.');
    }
  }

  hideAcceleratedCheckout();
  hideUnsupportedDiscountToast();
  patchCheckoutDestinations();
  document.addEventListener('DOMContentLoaded', () => {
    hideAcceleratedCheckout();
    hideUnsupportedDiscountToast();
    patchCheckoutDestinations();
  });
  new MutationObserver(() => { hideUnsupportedDiscountToast(); patchCheckoutDestinations(); }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'action'] });
  setInterval(patchCheckoutDestinations, 1000);

  const nativeSubmit = HTMLFormElement.prototype.submit;
  HTMLFormElement.prototype.submit = function() {
    if (isCheckoutTrigger(this) || this.querySelector('[name="checkout"]')) return openStripeCheckout({ preventDefault(){}, stopPropagation(){}, stopImmediatePropagation(){} });
    return nativeSubmit.apply(this, arguments);
  };
  const nativeRequestSubmit = HTMLFormElement.prototype.requestSubmit;
  if (nativeRequestSubmit) HTMLFormElement.prototype.requestSubmit = function(submitter) {
    if (isCheckoutTrigger(submitter) || isCheckoutTrigger(this) || this.querySelector('[name="checkout"]')) return openStripeCheckout({ preventDefault(){}, stopPropagation(){}, stopImmediatePropagation(){} });
    return nativeRequestSubmit.apply(this, arguments);
  };

  document.addEventListener('click', (event) => {
    const path = event.composedPath ? event.composedPath() : [];
    const trigger = (event.target && event.target.closest && event.target.closest('a,button,input[type="submit"],form')) || path.find(el => el && el.getAttribute && isCheckoutTrigger(el));
    if (isCheckoutTrigger(trigger)) openStripeCheckout(event);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart"]')) return;
    const submitter = event.submitter || document.activeElement;
    if (isCheckoutTrigger(submitter) || form.querySelector('[name="checkout"]')) openStripeCheckout(event);
  }, true);
})();
